import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { formatDistanceToNow } from "date-fns";
import { Send } from "lucide-react";
import type { MedicalTextIssueComment, CreateCommentData } from "@/types/issues";

interface CommentThreadProps {
  issueId: string;
  comments: MedicalTextIssueComment[];
  isLoading: boolean;
  onAddComment: (data: CreateCommentData) => void;
  isAddingComment: boolean;
}

export function CommentThread({ issueId, comments, isLoading, onAddComment, isAddingComment }: CommentThreadProps) {
  const [newComment, setNewComment] = useState("");

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!newComment.trim()) {
      return;
    }

    onAddComment({
      issue_id: issueId,
      comment_text: newComment.trim(),
    });

    setNewComment("");
  };

  if (isLoading) {
    return <div className="text-center text-gray-500 py-4">Loading comments...</div>;
  }

  return (
    <div className="space-y-4">
      {/* Comments List */}
      <div className="space-y-3 max-h-80 overflow-y-auto">
        {comments.length === 0 ? (
          <div className="text-center text-gray-500 py-4 border rounded-lg">
            No comments yet. Be the first to comment!
          </div>
        ) : (
          comments.map((comment) => (
            <div key={comment.id} className="border rounded-lg p-3 bg-gray-50">
              <div className="flex items-start justify-between mb-2">
                <div className="flex items-center gap-2">
                  {(comment as any).created_by_profile && (
                    <>
                      <div
                        className="w-4 h-4 rounded-full"
                        style={{
                          backgroundColor: (comment as any).created_by_profile.display_color,
                        }}
                      />
                      <span className="text-sm font-medium">{(comment as any).created_by_profile.display_name}</span>
                    </>
                  )}
                </div>
                <span className="text-xs text-gray-500">
                  {formatDistanceToNow(new Date(comment.created_at), { addSuffix: true })}
                </span>
              </div>
              <p className="text-sm text-gray-700 whitespace-pre-wrap">{comment.comment_text}</p>
            </div>
          ))
        )}
      </div>

      {/* Add Comment Form */}
      <form onSubmit={handleSubmit} className="border rounded-lg p-3 bg-white">
        <Textarea
          value={newComment}
          onChange={(e) => setNewComment(e.target.value)}
          placeholder="Add a comment..."
          className="mb-3 min-h-[60px] resize-none"
          disabled={isAddingComment}
        />
        <div className="flex justify-end">
          <Button type="submit" size="sm" disabled={!newComment.trim() || isAddingComment}>
            <Send className="w-4 h-4 mr-2" />
            {isAddingComment ? "Posting..." : "Post Comment"}
          </Button>
        </div>
      </form>
    </div>
  );
}
